import { MouseEvent, TouchEvent, Touch, useCallback, useRef } from 'react';
import { useEventListener, useRefCallback } from '@direct-frontend/stdlib';

import { GestureEvents } from '../../internal/types/gestureTypes';

/**
 * Вектор, выраженный координатами x и y
 */
type Vector = { x: number; y: number };

/**
 * Свойства состояния жеста, которые всегда присутствуют
 */
type GestureBaseState<T> = {
    /**
     * Координаты пальца пользователя в данный момент
     */
    currentPosition: Vector;

    /**
     * Координаты начала жеста
     */
    initialPosition: Vector;

    /**
     * Время начала жеста (timestamp)
     */
    startTime: number;

    /**
     * Исходный объект события
     */
    event: TouchEvent | MouseEvent;

    /**
     * Тип указателя (что инициировало жест)
     */
    pointerType: 'mouse' | 'touch';

    /**
     * Пользовательские данные
     */
    data?: T;
};

/**
 * Состояние жеста, когда он только начался (произошел mousedown/touchstart)
 */
type GestureStartState<T> = GestureBaseState<T> & {
    /**
     * Тип события жеста
     */
    type: 'start';

    /**
     * Было ли движение в жесте
     */
    hadMove: false;
};

/**
 * Состояние жеста, когда жест завершился без движения (обычно просто клик/тап)
 */
type GestureEndedWithoutMoveState<T> = GestureBaseState<T> & {
    /**
     * Тип события жеста
     */
    type: 'end';

    /**
     * Было ли движение в жесте
     */
    hadMove: false;
};

/**
 * Состояние жеста, в котором было движение (mousemove/touchmove)
 *
 * Поэтому оно содержит delta, velocity, elapsedTime, и подобные вычисляемые свойства
 */
type GestureHadMoveState<T> = GestureBaseState<T> & {
    /**
     * Тип события жеста
     */
    type: 'move' | 'end';

    /**
     * Было ли движение в жесте
     */
    hadMove: true;

    /**
     * Координаты пальца пользователя в момент предыдущего срабатывания функции
     */
    previousPosition: Vector;

    /**
     * Сдвиг по каждой из осей с момента начала жеста
     */
    movement: Vector;

    /**
     * Расстояние, пройденное с момента предыдущего вызова функции
     */
    delta: Vector;

    /**
     * Длительность жеста
     */
    elapsedTime: number;

    /**
     * Скорость движения пальца по каждой из осей
     */
    velocity: Vector;
};

/**
 * Объект состояния жеста
 */
type GestureState<T> = GestureStartState<T> | GestureEndedWithoutMoveState<T> | GestureHadMoveState<T>;

type StateChangeCallback<T> = (arg: Readonly<GestureState<T>>, setData: (data: T) => void) => void;

/**
 * Обновление позиции пальца/мыши во время жеста перемещения
 * @param state - состояние жеста
 * @param event - событие пальца/мыши
 * @param touch - касание пальцем
 */
function getUpdatedPosition<T>(
    state: GestureState<T>,
    event: MouseEvent<Element> | TouchEvent<Element>,
    touch?: Touch,
): Pick<
    GestureHadMoveState<T>,
    'hadMove' | 'previousPosition' | 'currentPosition' | 'movement' | 'delta' | 'velocity' | 'elapsedTime'
> {
    const previousPosition = state.currentPosition;

    // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const currentPosition = getClientPositions(touch ? touch : (event as MouseEvent<Element>));
    const delta = {
        x: currentPosition.x - (previousPosition || state.initialPosition).x,
        y: currentPosition.y - (previousPosition || state.initialPosition).y,
    };

    const movement = {
        x: currentPosition.x - state.initialPosition.x,
        y: currentPosition.y - state.initialPosition.y,
    };

    const elapsedTimeForPrevious = 'elapsedTime' in state ? state.elapsedTime : 0;
    const timeAfterPrevious = event.timeStamp - state.startTime - elapsedTimeForPrevious;

    const velocity = {
        x: delta.x / timeAfterPrevious || 0,
        y: delta.y / timeAfterPrevious || 0,
    };

    const elapsedTime = event.timeStamp - state.startTime;

    return {
        hadMove: true,
        previousPosition,
        currentPosition,
        movement,
        delta,
        velocity,
        elapsedTime,
    };
}

function getClientPositions(mouseEventOrTouch: { clientX: number; clientY: number }) {
    return {
        x: mouseEventOrTouch.clientX,
        y: mouseEventOrTouch.clientY,
    };
}

/**
 * Соответствие типа события и типа указателя
 */
const eventTypeToPointerType = {
    touchstart: 'touch',
    touchend: 'touch',
    touchmove: 'touch',
    touchcancel: 'touch',
    mousedown: 'mouse',
    mouseup: 'mouse',
    mousemove: 'mouse',
} as const;

/**
 * Возвращает true, если HTMLElement является contenteditable-элементом.
 * Проверяет как атрибут contenteditable="true", так и свойство isContentEditable
 * (которое учитывает наследование от родительских элементов).
 */
function isContentEditable(element: HTMLElement): boolean {
    return element.getAttribute('contenteditable') === 'true' || element.isContentEditable;
}

/**
 * Возвращает true, если элемент является редактируемым полем ввода
 * (input, textarea, contenteditable), в котором пользователь может выделять текст.
 * Используется для предотвращения перехвата drag-жеста при выделении текста.
 */
function isEditableElement(element: EventTarget | null): boolean {
    if (!(element instanceof HTMLElement)) {
        return false;
    }

    const closestEditableElement = element.closest('input, textarea, [contenteditable]');

    if (!closestEditableElement || !(closestEditableElement instanceof HTMLElement)) {
        return false;
    }

    if (closestEditableElement.tagName === 'INPUT' || closestEditableElement.tagName === 'TEXTAREA') {
        return true;
    }

    return isContentEditable(closestEditableElement);
}

/**
 * Возвращает true, если в документе есть активное выделение текста.
 * Охватывает два случая:
 * — выделение в обычном DOM (window.getSelection) — работает для текста вне input/textarea
 * — выделение внутри input/textarea (через selectionStart/selectionEnd)
 *
 * Используется для предотвращения перехвата drag-жеста когда пользователь
 * тянет selection handle для расширения уже существующего выделения.
 */
function hasActiveTextSelection(touchTarget: EventTarget | null): boolean {
    const windowSelection = window.getSelection();

    if (windowSelection && windowSelection.toString().length > 0) {
        return true;
    }

    // input и textarea хранят выделение отдельно от window.getSelection
    if (touchTarget instanceof HTMLInputElement || touchTarget instanceof HTMLTextAreaElement) {
        const selectionStart = touchTarget.selectionStart;
        const selectionEnd = touchTarget.selectionEnd;

        if (selectionStart !== null && selectionEnd !== null && selectionStart !== selectionEnd) {
            return true;
        }
    }

    return false;
}

/**
 * Унифицированный интерфейс для работы с
 * простыми жестами (где используется один палец или мышь)
 *
 * webdriver на псевдомобилке иногда криво кликает, вызывая только touchstart, без touchend,
 * поэтому при получении новых touchstart эвентов сразу после touchstart, мы реинициализируем состояние dnd жеста
 */
export const useDrag = <T>(onStateChange: StateChangeCallback<T>) => {
    const touchIdentifierRef = useRef<number>();
    const gestureStateRef = useRef<GestureState<T>>();
    const onStateChangeRef = useRef<StateChangeCallback<T>>();

    // Сброс drag-процесса при изменении selection (iOS: drag selection handle конфликтует с drag шторки).
    // На iOS selectionchange диспатчится синхронно во время touchmove при перемещении границы selection,
    // на Android же selection обработка не доходит до JS.
    // Срабатывает только если жест уже начался вне редактируемого элемента
    // (случай когда каретка находится в input/textarea/contenteditable обрабатывается в touchstart).
    const handleSelectionChange = useRefCallback(() => {
        const state = gestureStateRef.current;

        if (!state || state.pointerType !== 'touch') {
            return;
        }

        // Если шторка уже начала двигаться — имитируем завершение жеста,
        // чтобы useDrawerDrag вернул её в исходное положение
        if (state.hadMove) {
            state.type = 'end';
            onStateChangeRef.current?.(state, () => {});
        }

        gestureStateRef.current = undefined;
        touchIdentifierRef.current = undefined;
    });

    const documentRef = useRef(typeof document === 'undefined' ? null : document);
    useEventListener('selectionchange', handleSelectionChange, documentRef);

    const dragEventHandler = useCallback((event: TouchEvent | MouseEvent) => {
        const setData = (data: T) => {
            if (!gestureStateRef.current) {
                return;
            }

            gestureStateRef.current.data = data;
        };

        // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        if (!event.currentTarget.contains(event.target as HTMLElement)) {
            return;
        }

        const onStateChange = onStateChangeRef.current;
        let state = gestureStateRef.current;

        let touch;

        // поиск касания
        if (event.nativeEvent.constructor.name === 'TouchEvent') {
            // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            touch = Array.from((event as TouchEvent).changedTouches).find(
                item => item.identifier === touchIdentifierRef.current,
            );
        }

        const dragStartEventsHandlers = {
            touchstart: () => {
                if (event.type !== 'touchstart') {
                    return;
                }

                // На iOS выделение текста конфликтует с drag-жестом в двух случаях:
                // 1. Каретка стоит в input/textarea/contenteditable — пользователь тянет вниз
                //    для расширения выделения, а touchmove перехватывается как drag шторки.
                // 2. В документе уже есть активное выделение текста — пользователь тянет
                //    selection handle, а не шторку.
                // Решение: не инициализировать жест ни в одном из этих случаев.
                if (isEditableElement(event.target) || hasActiveTextSelection(event.target)) {
                    return;
                }

                // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                const changedTouches = (event as TouchEvent).changedTouches;
                if (changedTouches.length === 1 && changedTouches[0]) {
                    touch = changedTouches[0];
                    touchIdentifierRef.current = touch?.identifier;

                    const position = getClientPositions(touch);

                    gestureStateRef.current = state = {
                        type: 'start',
                        hadMove: false,
                        startTime: event.timeStamp,
                        initialPosition: position,
                        currentPosition: position,
                        pointerType: eventTypeToPointerType[event.type],
                        event,
                    };
                }
                return;
            },
            mousedown: () => {
                if (event.type !== 'mousedown') {
                    return;
                }

                // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                const position = getClientPositions(event as MouseEvent);

                gestureStateRef.current = state = {
                    type: 'start',
                    hadMove: false,
                    startTime: event.timeStamp,
                    initialPosition: position,
                    currentPosition: position,
                    pointerType: eventTypeToPointerType[event.type],
                    event,
                };
                return;
            },
        } as const;

        const isDragStartEventType = (eventType: string): eventType is keyof typeof dragStartEventsHandlers => {
            return eventType in dragStartEventsHandlers;
        };

        // начало перемещения объекта
        if (!state) {
            if (isDragStartEventType(event.type)) {
                dragStartEventsHandlers[event.type]();
            }
        }

        // перемещение объекта
        if (state && event) {
            state.event = event;

            // Реинициализируем состояние
            if (isDragStartEventType(event.type)) {
                dragStartEventsHandlers[event.type]();
                onStateChange?.(state, setData);

                return;
            }

            switch (event.type) {
                case 'touchmove':
                case 'mousemove': {
                    // Обрабатываем только события того же типа курсора, что и изначальное
                    if (state.pointerType !== eventTypeToPointerType[event.type]) {
                        break;
                    }

                    const newState: GestureHadMoveState<T> = {
                        ...state,
                        type: 'move',
                        ...getUpdatedPosition(state, event, touch),
                    };

                    Object.assign(state, newState);

                    break;
                }

                // завершение перемещения объекта
                case 'touchend':
                case 'touchcancel':
                case 'mouseup': {
                    state.type = 'end';
                    gestureStateRef.current = undefined;

                    if (touch) {
                        touchIdentifierRef.current = undefined;
                    }
                    break;
                }

                default:
                    break;
            }

            onStateChange?.(state, setData);
        }
    }, []);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    onStateChangeRef.current = onStateChange;

    const dragProps: GestureEvents = {
        onTouchStart: dragEventHandler,
        onTouchMove: dragEventHandler,
        onTouchEnd: dragEventHandler,
        onTouchCancel: dragEventHandler,

        onMouseDown: dragEventHandler,
        onMouseMove: dragEventHandler,
        onMouseUp: dragEventHandler,
    };

    return {
        dragProps,
    };
};
